/**
 * @module app.datepickerDirective
 */
import appBase from './index.js';
import {hasNativeDateInput} from "./utils";
import {noNativeDateInput} from "./hiddenfeature";


/**
 * @constructor
 * @param {angular.Scope} $scope Angular scope
 * @param {jQuery} $element Element.
 * @param {angular.$filter} $filter Filter service.
 * @ngInject
 * @struct
 * @private
 */
class DatepickerController {

  constructor($scope, $element, $filter) {

    /**
     * @type {angular.Scope}
     * @private
     */
    this.scope_ = $scope;

    /**
     * @type {jQuery}
     * @private
     */
    this.datepickerInput_ = $element.find('.datepicker input');

    /**
     * @const {function(Date, string)}
     * @private
     */
    this.dateFilter_ = $filter('date');

    /**
     * @const {boolean}
     * @export
     */
    this.useNativeDateInput = !noNativeDateInput() && hasNativeDateInput();

    /**
     * @type {string}
     * @export
     */
    this.nativeMinDate;

    /**
     * @type {string}
     * @export
     */
    this.placeholder = this.scope_['placeholder'] || '';

    /**
     * @type {Object}
     * @private
     * Angular and Datepicker use different codes for the same format!
     */
    this.dateFormats_ = {
      'dp': 'dd.mm.yy',
      'ng': 'dd.MM.yyyy'
    };

    /**
     * @private
     * Cache Date Object that is passed as ng-model to native date input.
     * This avoids an infinite digest loop.
     */
    this.dateObjectCache_ = {};

    const initialDate = this.scope_['date'];
    const onlyFutureDates = this.scope_['onlyFutureDates'] || false;
    const datepickerOptions = {
      'showOn': 'None',
      'onSelect': this.onSelect_.bind(this),
      'onClose': this.onClose_.bind(this),
      'dateFormat': this.dateFormats_['dp'],
      'constrainInput': true
    };
    if (onlyFutureDates) {
      datepickerOptions['minDate'] = initialDate;
      datepickerOptions['maxDate'] = '+1Y';
      const parsedInitialDate = this.parseDate_(initialDate);
      this.nativeMinDate = this.dateFilter_(parsedInitialDate, 'yyyy-MM-dd');
    }
    this.datepickerInput_.datepicker(datepickerOptions);
    this.datepickerInput_.datepicker('setDate', initialDate);

    this.scope_.$watch('date', (newValue, oldValue) => {
      if (newValue != oldValue) {
        this.datepickerInput_.datepicker('setDate', newValue);
      }
    });

    // Open date picker if the user enters the input field.
    this.datepickerInput_.on('focus click', () => {
      this.show();
    });
  }

  /**
   * @param {Date} date
   * @return {string}
   * @private
   */
  formatDate_(date) {
    return this.dateFilter_(date, this.dateFormats_['ng']);
  }


  /**
   * @param {string} dateString
   * @return {Date}
   * @private
   */
  parseDate_(dateString) {
    return $['datepicker']['parseDate'](this.dateFormats_['dp'], dateString);
  }


  /**
   * @param {Date} newDate
   * @export
   */
  nativeInputGetSet(newDate) {
    if (arguments.length) {
      // set new date
      if (newDate) {
        this.scope_['date'] = this.formatDate_(newDate);
      } else {
        this.scope_['date'] = null;
      }
    } else {
      // get date
      const dateString = this.scope_['date'];
      if (dateString) {
        if (!this.dateObjectCache_[dateString]) {
          this.dateObjectCache_ = {
            [dateString]: this.parseDate_(this.scope_['date'])
          };
        }
        return this.dateObjectCache_[dateString];
      }
    }
  }


  /**
   * @export
   */
  show() {
    if (!this.datepickerInput_.datepicker('widget').is(':visible')) {
      this.datepickerInput_.datepicker('show');
    }
  }


  /**
   * @param {number} numberOfDays
   * @export
   */
  setDay(numberOfDays) {
    const date = new Date(this.datepickerInput_.datepicker('getDate'));
    date.setDate(date.getDate() + numberOfDays);
    this.scope_['date'] = this.formatDate_(date);
  }


  /**
   * @private
   */
  onSelect_() {
    const date = this.datepickerInput_.datepicker('getDate');
    console.assert(date instanceof Date);
    this.scope_['date'] = this.formatDate_(date);
    this.scope_.$apply();
  }


  /**
   * @param {string} dateText Selected date in datepicker.
   * @private
   */
  onClose_(dateText) {
    if (dateText === '') {
      this.scope_['date'] = null;
      this.scope_.$apply();
    } else {
      this.onSelect_();
    }
  }


}

appBase.controller('appDatepickerController', DatepickerController);


/**
 * @return {angular.Directive} The directive specs.
 * @ngInject
 */
const appDatepicker = function() {
  return {
    restrict: 'E',
    replace: true,
    transclude: false,
    controller: 'appDatepickerController',
    controllerAs: 'dpCtrl',
    scope: {
      'date': '=ngModel',
      'onlyFutureDates': '<?onlyFutureDates',
      'placeholder': '@?placeholder'
    },
    templateUrl: 'partials/datepicker.html'
  };
};

appBase.directive('appDatepicker', appDatepicker);


export default appDatepicker;
